import { collection, deleteDoc, doc, getDocs, limit, query, setDoc, updateDoc, where, arrayRemove, arrayUnion, serverTimestamp, getDoc } from 'firebase/firestore';
import { db, ensureUser } from '../firebase';
import type { Work, Deal } from '../data/types';
import type { Profile } from './state';
import { loadCampaign, type Campaign } from './campaign';

/**
 * The feed's own data (10 §7, §12): the person's posts, hearts, delete and
 * report. Posts are written by the backend; everything here is a read or an
 * owner's edit under the kindling-* rules.
 */
export interface FeedPost {
  postId: string;
  uid: string;
  challengeId: string;
  deal: Deal;
  day: number;
  kind: 'text' | 'photo' | 'invite' | 'character' | 'film';
  /** a first film: the clip, once rolled */
  videoUrl?: string;
  filmStatus?: string;
  characterName?: string;
  headline: string;
  cardUrl: string;
  invite: boolean;
  visibility: 'friends' | 'me';
  hearts: string[];
  createdAt: number;
  renders?: { url: string; idea: string; by: string }[];
  /** the pictures the owner has turned over (lib/reveal.ts) */
  opened?: string[];
  renderStatus?: string;
  /** the told story's storyboard (lib/storyboard.ts); only its id and status are on the post */
  storyId?: string;
  storyboardStatus?: string;
  warmupSec?: number;
  audioUrl?: string;
  audioSec?: number;
  /** what was typed, minus the photo and the audio (lib/posts.ts): the person's own words, one idea at a time */
  work?: Work;
}

function toPost(id: string, d: Record<string, unknown>): FeedPost {
  const ts = d.createdAt as { toMillis?: () => number } | undefined;
  return {
    postId: id,
    uid: String(d.uid),
    challengeId: String(d.challengeId),
    deal: (d.deal as Deal) || {},
    day: Number(d.day) || 0,
    kind: (d.kind as FeedPost['kind']) || 'text',
    headline: String(d.headline || ''),
    cardUrl: String(d.cardUrl || ''),
    invite: !!d.invite,
    visibility: (d.visibility as FeedPost['visibility']) || 'friends',
    hearts: (d.hearts as string[]) || [],
    createdAt: ts?.toMillis ? ts.toMillis() : 0,
    renders: (d.renders as FeedPost['renders']) || [],
    opened: (d.opened as string[] | undefined) || [],
    renderStatus: d.renderStatus as string | undefined,
    // a story told inside an exercise (A Story About a Friend) is storyboarded under the post's own id
    storyId: (d.storyId as string | undefined) || (d.storyboardStatus ? id : undefined),
    storyboardStatus: d.storyboardStatus as string | undefined,
    warmupSec: d.warmupSec as number | undefined,
    audioUrl: d.audioUrl as string | undefined,
    audioSec: d.audioSec as number | undefined,
    characterName: d.characterName as string | undefined,
    videoUrl: d.videoUrl as string | undefined,
    filmStatus: d.filmStatus as string | undefined,
    work: (d.work as Work) || undefined,
  };
}

export async function listMyPosts(max = 60): Promise<FeedPost[]> {
  const user = await ensureUser();
  // Single-field query (no composite index needed); newest first, sorted here.
  const q = query(collection(db, 'kindling-posts'), where('uid', '==', user.uid), limit(max));
  const snap = await getDocs(q);
  return snap.docs.map((d) => toPost(d.id, d.data())).filter((p) => !p.invite).sort((a, b) => b.createdAt - a.createdAt);
}

/** One heart, no number, no effect (10 §7). Toggles the person's own heart on a post they can see. */
export async function toggleHeart(post: FeedPost): Promise<boolean> {
  const user = await ensureUser();
  const mine = post.hearts.includes(user.uid);
  await updateDoc(doc(db, 'kindling-posts', post.postId), { hearts: mine ? arrayRemove(user.uid) : arrayUnion(user.uid) });
  return !mine;
}

export async function setVisibility(postId: string, visibility: 'friends' | 'me'): Promise<void> {
  await updateDoc(doc(db, 'kindling-posts', postId), { visibility });
}

export async function deletePost(postId: string): Promise<void> {
  await deleteDoc(doc(db, 'kindling-posts', postId));
}

/** A report hides the post for the reporter (locally) and writes a row the owner can read (10 §7). */
export async function reportPost(postId: string, reason?: string): Promise<void> {
  const user = await ensureUser();
  await setDoc(doc(collection(db, 'kindling-reports')), { postId, byUid: user.uid, reason: reason || null, createdAt: serverTimestamp(), handled: false });
  try {
    const key = 'kindling.hidden.v1';
    const hidden: string[] = JSON.parse(localStorage.getItem(key) || '[]');
    if (!hidden.includes(postId)) localStorage.setItem(key, JSON.stringify([...hidden, postId]));
  } catch { /* ignore */ }
}

export function hiddenPostIds(): Set<string> {
  try { return new Set<string>(JSON.parse(localStorage.getItem('kindling.hidden.v1') || '[]')); } catch { return new Set(); }
}

/**
 * The person's server-side record (kindling-users/{uid}). `profile` is the
 * canonical road (16 §4, written by profile-sync); the flat fields beside it
 * are what the backend reads. `email` may only be written from a token that
 * has verified it (firestore.rules), so the reminder cron can trust it.
 */
export interface UserDoc { name?: string; path?: 'writing' | 'drawing'; anchor?: { kind: string; time?: string }; tz?: string; email?: string; crew?: 'on' | 'off' | 'unasked'; madeDays?: number; reminders?: { off?: boolean }; profile?: Partial<Profile>; campaign?: Campaign;
  /** typed as a way to reach them and never proved (lib/contact.ts): nothing that trusts `email` reads it */
  contactEmail?: string; contactEmailFrom?: string; contactEmailAt?: number }

export async function readUserDoc(): Promise<UserDoc | null> {
  const user = await ensureUser();
  const snap = await getDoc(doc(db, 'kindling-users', user.uid));
  return snap.exists() ? (snap.data() as UserDoc) : null;
}

export async function writeUserDoc(patch: Partial<UserDoc>): Promise<void> {
  const user = await ensureUser();
  // Firestore refuses undefined values; drop them rather than fail the whole write.
  const clean = JSON.parse(JSON.stringify(patch));
  // Where they came from rides along on the first write, so retention can be
  // read per campaign later. merge:true and first-touch capture mean it is
  // written once and never moves.
  const campaign = loadCampaign();
  const stamp = campaign ? { campaign } : {};
  await setDoc(doc(db, 'kindling-users', user.uid), { ...stamp, ...clean, updatedAt: serverTimestamp() }, { merge: true });
}

export interface Friend { uid: string; name?: string; via: string; since: number }

export async function listFriends(): Promise<Friend[]> {
  const user = await ensureUser();
  const snap = await getDocs(collection(db, 'kindling-friends', user.uid, 'friends'));
  return snap.docs.map((d) => { const x = d.data(); const ts = x.since as { toMillis?: () => number } | undefined; return { uid: d.id, name: x.name as string | undefined, via: String(x.via || ''), since: ts?.toMillis ? ts.toMillis() : 0 }; });
}

/** Friends' posts set to Friends (the rules check the friendship on the owner's list). */
export async function listFriendsPosts(friends: Friend[], perFriend = 20): Promise<(FeedPost & { byName?: string })[]> {
  const out: (FeedPost & { byName?: string })[] = [];
  await Promise.all(friends.map(async (fr) => {
    try {
      const q = query(collection(db, 'kindling-posts'), where('uid', '==', fr.uid), where('visibility', '==', 'friends'), limit(perFriend));
      const snap = await getDocs(q);
      snap.docs.forEach((d) => { const p = toPost(d.id, d.data()); if (!p.invite) out.push({ ...p, byName: fr.name }); });
    } catch (err) { console.warn('[kindling] friend posts', fr.uid, err); }
  }));
  return out.sort((a, b) => b.createdAt - a.createdAt);
}
